import { Injectable, Inject } from "@angular/core";
import { Store } from "@ngrx/store";
import { AppState } from "../models/app.state";
import { ENV_VARS } from "../environment-variables/environment-variables.module";
import { HttpClient } from "@angular/common/http";
import { ISupportAction, SupportAction } from "../models/support-action";
import { UserState } from "../store/reducers/user.reducer";
import * as SupportActionActions from "../store/actions/support-action.actions";
import { FhirService } from "./fhir.service";
import { v4 } from "uuid";
import {
  ProviderLevel,
  SupportedDiabetesType,
  ActionType,
  DiaprofilTopic,
  Score,
  DanishMunicipality,
  DanishRegion
} from "../models/types";

export function uuid(): string {
  return v4().replace(/-/g, "");
}

interface SupportActionSearchResult {
  "@id": string;
  batching: {
    "@id": string;
    first: string;
    last: string;
    next: string;
  };
  items: SupportActionResult[];
  items_total: number;
}

interface SupportActionResult {
  "@id": string;
  id: string;
  sourceUrl: string;
  title: string;
  description: string;
  providerName: string;
  providerMunicipalities: DanishMunicipality[];
  url: URL;
  phoneNumber: string;
  actionTypeOther: string;
  actionTopicOther: string;
  questionIds: string[];
  consultationTopicIds: string[];
  patientWishIds: string[];
  consultationTopicIdsVersion2: string[];
  scoreIds: Score[];
  created: string;
  modified: string;
  availableForOtherOrganizations: boolean;
  providerRegions: DanishRegion[];
  // plone.restapi returns vocabulary based fields with a dict: {'title': string, 'token': string}
  providerLevel: { title: string; token: ProviderLevel };
  supportForDiabetesType: { title: string; token: SupportedDiabetesType };
  actionType: { title: string; token: ActionType };
  actionTopics: { title: string; token: DiaprofilTopic }[];
}

@Injectable()
export class SupportActionsService {
  supportActions: SupportAction[];
  userState: UserState;
  baseUrl: string;
  endpoints: { [key: string]: string };
  constructor(
    public http: HttpClient,
    public store: Store<AppState>,
    public fhir: FhirService,
    @Inject(ENV_VARS) public envVars
  ) {
    this.baseUrl = envVars.API_URL + "fhirdatabase/";
    this.endpoints = {
      supportActions: "@supportaction"
    };
    this.store
      .select(state => state.user)
      .subscribe(user => {
        this.userState = user;
      });
    this.store
      .select(state => state.supportActions)
      .subscribe(supportActions => {
        this.supportActions = supportActions.map(
          action => new SupportAction(action)
        );
      });
  }

  deleteSupportAction(
    supportAction: Partial<ISupportAction>,
    extraAction?: (result: ISupportAction) => void
  ) {
    return this.http
      .delete(
        `${this.baseUrl}${this.userState.organizationUuid}/${this.endpoints.supportActions}/${supportAction.id}`
      )
      .subscribe(result => {
        this.store.dispatch(
          new SupportActionActions.DeleteAction({
            id: supportAction.id
          })
        );
      });
  }

  patchSupportAction(
    supportAction: Partial<ISupportAction>,
    extraAction?: (result: ISupportAction) => void
  ) {
    return this.http
      .patch<ISupportAction>(supportAction.sourceUrl, supportAction)
      .subscribe(
        result => {
          this.store.dispatch(
            new SupportActionActions.ReplaceAction({
              id: supportAction.id,
              sourceUrl: supportAction.sourceUrl,
              title: supportAction.title,
              description: supportAction.description,
              providerName: supportAction.providerName,
              providerLevel: supportAction.providerLevel,
              providerRegions: supportAction.providerRegions,
              providerMunicipalities: supportAction.providerMunicipalities,
              url: supportAction.url,
              phoneNumber: supportAction.phoneNumber,
              supportForDiabetesType: supportAction.supportForDiabetesType,
              actionType: supportAction.actionType,
              actionTypeOther: supportAction.actionTypeOther,
              actionTopics: supportAction.actionTopics,
              actionTopicOther: supportAction.actionTopicOther,
              questionIds: supportAction.questionIds,
              consultationTopicIds: supportAction.consultationTopicIds,
              consultationTopicIdsVersion2:
                supportAction.consultationTopicIdsVersion2,
              patientWishIds: supportAction.patientWishIds,
              scoreIds: supportAction.scoreIds,
              created: supportAction.created,
              modified: supportAction.modified,
              availableForOtherOrganizations:
                supportAction.availableForOtherOrganizations
            })
          );
          if (extraAction !== undefined) {
            extraAction(result);
          }
        },
        // Errors will call this callback instead:
        err => {
          console.log(err);
        }
      );
  }
  postSupportAction(
    supportAction: Partial<ISupportAction>,
    extraAction?: (result: SupportActionResult) => void
  ) {
    return this.http
      .post<SupportActionResult>(
        `${this.baseUrl}${this.userState.organizationUuid}/${this.endpoints.supportActions}`,
        {
          "@type": "SupportAction",
          id: uuid(),
          ...supportAction
        }
      )
      .subscribe(
        result => {
          this.store.dispatch(
            new SupportActionActions.ReplaceAction({
              id: result.id,
              sourceUrl: result["@id"],
              title: result.title,
              description: result.description,
              providerName: result.providerName,
              providerLevel: result.providerLevel.token,
              providerRegions: result.providerRegions,
              providerMunicipalities: result.providerMunicipalities,
              url: result.url,
              phoneNumber: result.phoneNumber,
              supportForDiabetesType: result.supportForDiabetesType.token,
              actionType: result.actionType.token,
              actionTypeOther: result.actionTypeOther,
              actionTopics: Array.isArray(result.actionTopics)
                ? result.actionTopics.map(actionTopic => actionTopic.token)
                : null,
              actionTopicOther: result.actionTopicOther,
              questionIds: result.questionIds,
              consultationTopicIds: result.consultationTopicIds,
              patientWishIds: result.patientWishIds,
              consultationTopicIdsVersion2: result.consultationTopicIdsVersion2,
              scoreIds: result.scoreIds,
              created: result.created,
              modified: result.modified,
              availableForOtherOrganizations:
                result.availableForOtherOrganizations
            })
          );
          if (extraAction !== undefined) {
            extraAction(result);
          }
        },
        // Errors will call this callback instead:
        err => {
          console.log(err);
        }
      );
  }

  getSupportActions(
    extraAction?: (result: SupportActionSearchResult) => void,
    url?: string
  ) {
    if (url === undefined) {
      url = `${this.baseUrl}${this.userState.organizationUuid}/${this.endpoints.supportActions}?portal_type=SupportAction`;
    }
    return this.http.get<SupportActionSearchResult>(url).subscribe(
      result => {
        if (
          result.hasOwnProperty("batching") &&
          result.batching.next !== null &&
          result.batching["@id"] !== result.batching.last
        ) {
          this.getSupportActions(extraAction, result.batching.next);
        }
        const mappedItems = result.items.map(item => {
          console.log(item.consultationTopicIdsVersion2);
          return {
            id: item.id,
            sourceUrl: item["@id"],
            title: item.title,
            description: item.description,
            providerName: item.providerName,
            providerLevel: item.providerLevel.token,
            providerRegions: item.providerRegions,
            providerMunicipalities: item.providerMunicipalities,
            url: item.url,
            phoneNumber: item.phoneNumber,
            supportForDiabetesType: item.supportForDiabetesType.token,
            actionType: item.actionType.token,
            actionTypeOther: item.actionTypeOther,
            actionTopics: Array.isArray(item.actionTopics)
              ? item.actionTopics.map(actionTopic => actionTopic.token)
              : null,
            actionTopicOther: item.actionTopicOther,
            questionIds: item.questionIds,
            consultationTopicIds: item.consultationTopicIds,
            patientWishIds: item.patientWishIds,
            consultationTopicIdsVersion2: item.consultationTopicIdsVersion2,
            scoreIds: item.scoreIds,
            created: item.created,
            modified: item.modified,
            availableForOtherOrganizations: item.availableForOtherOrganizations
          };
        });
        this.store.dispatch(
          new SupportActionActions.ReplaceMultipleAction(mappedItems)
        );
        if (extraAction !== undefined) {
          extraAction(result);
        }
      },
      // Errors will call this callback instead:
      err => {
        console.log(err);
      }
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/services/support-actions.service.ts